/**
 * My Account → Adresy — CRUD UI for saved addresses.
 *
 * Sterowane REST endpoints `/hn/v1/account/addresses` z `inc/myaccount-addresses.php`.
 * Initial HTML render PHP (SEO + no-JS fallback). JS handluje:
 *  - Add / Edit form toggle + submit
 *  - Set primary (POST /id/primary)
 *  - Delete z confirm
 *  - Optimistic UI: po sukcesie reload widoku (kompromis kod-vs-złożoność, no live patch).
 */

const REST_PATH = 'hn/v1/account/addresses';

function getRestBase() {
    if (window.wpApiSettings && window.wpApiSettings.root) return window.wpApiSettings.root;
    return '/wp-json/';
}

/* WP REST wymaga nonce dla zmodyfikowanych użytkowników (X-WP-Nonce header) — patrz
 * `wp_localize_script('wp-api', 'wpApiSettings', ['nonce' => wp_create_nonce('wp_rest')])`. */
function authHeaders() {
    const h = { 'Content-Type': 'application/json' };
    if (window.wpApiSettings && window.wpApiSettings.nonce) {
        h['X-WP-Nonce'] = window.wpApiSettings.nonce;
    }
    return h;
}

async function apiCall(path, options = {}) {
    const res = await fetch(getRestBase() + path, {
        credentials: 'same-origin',
        ...options,
        headers: { ...authHeaders(), ...(options.headers || {}) },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        const err = new Error((data && data.message) || 'Błąd');
        err.data = data;
        throw err;
    }
    return data;
}

function showFormMsg(msgEl, text, isError) {
    if (!msgEl) return;
    msgEl.textContent = text;
    msgEl.hidden = false;
    msgEl.classList.toggle('is-error', !!isError);
    msgEl.classList.toggle('is-success', !isError);
}

function resetForm(formWrap) {
    const fields = formWrap.querySelectorAll('[data-field]');
    fields.forEach((f) => {
        if (f.type === 'checkbox') f.checked = false;
        else f.value = '';
    });
    const msg = formWrap.querySelector('[data-hn-form-msg]');
    if (msg) { msg.hidden = true; msg.textContent = ''; }
    const title = formWrap.querySelector('[data-hn-form-title]');
    if (title) title.textContent = 'Dodaj nowy adres';
}

function fillForm(formWrap, address) {
    Object.keys(address).forEach((key) => {
        const el = formWrap.querySelector(`[data-field="${key}"]`);
        if (!el) return;
        if (el.type === 'checkbox') el.checked = !!address[key];
        else el.value = address[key] || '';
    });
    const title = formWrap.querySelector('[data-hn-form-title]');
    if (title) title.textContent = 'Edytuj adres';
}

function gatherForm(formWrap) {
    const payload = {};
    formWrap.querySelectorAll('[data-field]').forEach((el) => {
        const key = el.dataset.field;
        if (el.type === 'checkbox') payload[key] = el.checked;
        else payload[key] = el.value.trim();
    });
    return payload;
}

function bindItem(item, root) {
    const id = item.dataset.addressId;
    if (!id) return;

    const primaryBtn = item.querySelector('[data-hn-address-primary]');
    if (primaryBtn) {
        primaryBtn.addEventListener('click', async () => {
            primaryBtn.disabled = true;
            try {
                await apiCall(`${REST_PATH}/${id}/primary`, { method: 'POST' });
                window.location.reload();
            } catch (e) {
                primaryBtn.disabled = false;
                alert(e.message);
            }
        });
    }

    const editBtn = item.querySelector('[data-hn-address-edit]');
    if (editBtn) {
        editBtn.addEventListener('click', async () => {
            /* Fetch fresh state — w razie gdyby user dodał coś w innej karcie. */
            try {
                const data = await apiCall(REST_PATH);
                const addr = (data.addresses || []).find((a) => a.id === id);
                if (!addr) { alert('Adres nie istnieje.'); return; }
                openForm(root, addr);
            } catch (e) {
                alert(e.message);
            }
        });
    }

    const delBtn = item.querySelector('[data-hn-address-delete]');
    if (delBtn) {
        delBtn.addEventListener('click', async () => {
            const label = item.querySelector('.hn-addresses__item-label')?.textContent || 'ten adres';
            if (!confirm(`Usunąć adres „${label}"?`)) return;
            delBtn.disabled = true;
            try {
                await apiCall(`${REST_PATH}/${id}`, { method: 'DELETE' });
                window.location.reload();
            } catch (e) {
                delBtn.disabled = false;
                alert(e.message);
            }
        });
    }
}

function openForm(root, address) {
    const formWrap = root.querySelector('[data-hn-addresses-form]');
    if (!formWrap) return;
    resetForm(formWrap);
    if (address) fillForm(formWrap, address);
    formWrap.hidden = false;
    formWrap.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const firstField = formWrap.querySelector('[data-field="label"]');
    if (firstField) setTimeout(() => firstField.focus(), 200);
}

function closeForm(root) {
    const formWrap = root.querySelector('[data-hn-addresses-form]');
    if (formWrap) {
        formWrap.hidden = true;
        resetForm(formWrap);
    }
}

async function submitForm(root, e) {
    e.preventDefault();
    const formWrap = root.querySelector('[data-hn-addresses-form]');
    const submitBtn = formWrap.querySelector('[data-hn-form-submit]');
    const msgEl = formWrap.querySelector('[data-hn-form-msg]');
    const payload = gatherForm(formWrap);
    const isEdit = !!payload.id;

    /* Client-side basic check — Backend re-walida. */
    if (!payload.label || !payload.first_name || !payload.last_name ||
        !payload.address_1 || !payload.postcode || !payload.city) {
        showFormMsg(msgEl, 'Uzupełnij wymagane pola.', true);
        return;
    }
    if (!/^[0-9]{2}-[0-9]{3}$/.test(payload.postcode)) {
        showFormMsg(msgEl, 'Kod pocztowy w formacie XX-XXX.', true);
        return;
    }

    submitBtn.disabled = true;
    const origLabel = submitBtn.textContent;
    submitBtn.textContent = 'Zapisuję…';

    try {
        if (isEdit) {
            await apiCall(`${REST_PATH}/${payload.id}`, {
                method: 'PUT',
                body: JSON.stringify(payload),
            });
        } else {
            await apiCall(REST_PATH, {
                method: 'POST',
                body: JSON.stringify(payload),
            });
        }
        window.location.reload();
    } catch (e) {
        const fields = e.data && e.data.data && e.data.data.fields;
        if (fields) {
            const first = Object.keys(fields)[0];
            showFormMsg(msgEl, `${fields[first]}`, true);
        } else {
            showFormMsg(msgEl, e.message || 'Nie udało się zapisać.', true);
        }
        submitBtn.disabled = false;
        submitBtn.textContent = origLabel;
    }
}

export function initMyAccountAddresses() {
    const root = document.querySelector('[data-hn-addresses]');
    if (!root) return;

    root.querySelectorAll('.hn-addresses__item[data-address-id]').forEach((item) => bindItem(item, root));

    const addTrigger = root.querySelector('[data-hn-addresses-add-trigger]');
    if (addTrigger) addTrigger.addEventListener('click', () => openForm(root, null));

    const cancelBtn = root.querySelector('[data-hn-form-cancel]');
    if (cancelBtn) cancelBtn.addEventListener('click', () => closeForm(root));

    const formEl = root.querySelector('[data-hn-addresses-form-el]');
    if (formEl) formEl.addEventListener('submit', (e) => submitForm(root, e));
}
